import React, { useEffect, useState } from 'react';
import { LoadingAnimatedInline } from '../components/Loading';

import { useAuth } from '../auth/AuthContext';
import {
  type AdminConfirmationPopupProps,
  AdminConfirmationPopup
} from '../components/AdminConfirmationPopup';

type RemoteGroup = {
  id: string;
  name: string;
  users: string[];
};

const isValidEmail = (value: string) =>
  /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value.trim());

const EmailGroups: React.FC = () => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [groups, setGroups] = useState<RemoteGroup[]>([]);

  const [searchQuery, setSearchQuery] = useState('');
  const [newGroupName, setNewGroupName] = useState('');
  const [isCreating, setIsCreating] = useState(false);

  const [expandedGroupId, setExpandedGroupId] = useState<string | null>(null);
  const [newEmailInputs, setNewEmailInputs] = useState<Record<string, string>>(
    {}
  );

  const [confirmation, setConfirmation] =
    useState<AdminConfirmationPopupProps | null>(null);

  useEffect(() => {
    fetchGroups();
  }, []);

  const fetchGroups = async () => {
    setLoading(true);
    try {
      if (!user) throw new Error('Not authenticated');
      const token = await user.getIdToken();
      const res = await fetch('/api/admin/email/groups', {
        method: 'GET',
        headers: { Authorization: `Bearer ${token}` },
        credentials: 'same-origin'
      });
      if (!res.ok) throw new Error('Failed to fetch groups');
      const data = await res.json();
      setGroups(data.groups || []);
    } catch (e) {
      console.error('Error fetching groups:', e);
      setError(e instanceof Error ? e.message : 'Failed to fetch groups');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateGroup = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newGroupName.trim()) return;
    setIsCreating(true);

    try {
      if (!user) throw new Error('Not authenticated');
      const token = await user.getIdToken();
      const res = await fetch('/api/admin/email/groups/create', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        credentials: 'same-origin',
        body: JSON.stringify({ name: newGroupName.trim() })
      });
      if (!res.ok) throw new Error('Failed to create group');
      const data = await res.json();
      setGroups((prev) => [...prev, data.group]);
      setNewGroupName('');
    } catch (e) {
      console.error('Error creating group:', e);
      setError(e instanceof Error ? e.message : 'Failed to create group');
    } finally {
      setIsCreating(false);
    }
  };

  const handleAddEmail = async (groupId: string) => {
    const email = (newEmailInputs[groupId] || '').trim();
    if (!email) return;
    if (!isValidEmail(email)) {
      alert('Please enter a valid email address.');
      return;
    }
    try {
      if (!user) throw new Error('Not authenticated');
      const token = await user.getIdToken();
      const res = await fetch('/api/admin/email/groups/addEmail', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        credentials: 'same-origin',
        body: JSON.stringify({ groupId, email })
      });
      if (!res.ok) throw new Error('Failed to add email');
      setGroups((prev) =>
        prev.map((g) =>
          g.id === groupId && !g.users.includes(email)
            ? { ...g, users: [...g.users, email] }
            : g
        )
      );
      setNewEmailInputs((prev) => ({ ...prev, [groupId]: '' }));
    } catch (e) {
      console.error('Error adding email:', e);
      alert('Failed to add email.');
    }
  };

  const handleRemoveEmail = (groupId: string, email: string) => {
    setConfirmation({
      innerHTML: (
        <div className="w-full flex flex-col items-center gap-4">
          <p className="w-full text-lg text-center">
            Remove <strong>{email}</strong> from this group?
          </p>
        </div>
      ),
      onConfirm: async () => {
        setConfirmation(null);
        try {
          if (!user) throw new Error('Not authenticated');
          const token = await user.getIdToken();
          const res = await fetch('/api/admin/email/groups/removeEmail', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              Authorization: `Bearer ${token}`
            },
            credentials: 'same-origin',
            body: JSON.stringify({ groupId, email })
          });
          if (!res.ok) throw new Error('Failed to remove email');
          setGroups((prev) =>
            prev.map((g) =>
              g.id === groupId
                ? { ...g, users: g.users.filter((u) => u !== email) }
                : g
            )
          );
        } catch (e) {
          console.error('Error removing email:', e);
          alert('Failed to remove email.');
        }
      },
      onCancel: () => setConfirmation(null)
    });
  };

  const filteredGroups = groups.filter((g) =>
    g.name.toLowerCase().includes(searchQuery.toLowerCase())
  );

  if (loading) return <LoadingAnimatedInline />;

  return (
    <div className="p-6 h-full flex flex-col gap-6">
      <h2 className="text-2xl font-bold">Email Groups</h2>
      {error && (
        <div className="p-3 bg-red-100 text-red-700 rounded-md">{error}</div>
      )}
      {/* Search */}
      <input
        type="search"
        placeholder="Search groups by name..."
        value={searchQuery}
        onChange={(e) => setSearchQuery(e.target.value)}
        className="w-full border border-light-border p-2 rounded-md"
      />

      {/* Create group form */}
      <form onSubmit={handleCreateGroup} className="flex gap-2">
        <input
          type="text"
          placeholder="New group name"
          value={newGroupName}
          onChange={(e) => setNewGroupName(e.target.value)}
          className="flex-1 border border-light-border p-2 rounded-md focus:outline-none focus:ring-2 focus:ring-light-main"
        />
        <button
          type="submit"
          disabled={isCreating || !newGroupName.trim()}
          className="px-4 py-2 bg-light-main text-white rounded-md hover:bg-light-main-hover transition-colors duration-200 ease-in-out disabled:opacity-50 disabled:cursor-not-allowed font-medium"
        >
          {isCreating ? 'Creating...' : 'Create Group'}
        </button>
      </form>

      {/* Groups table */}
      <div className="flex-1 overflow-y-auto border border-light-border rounded-md">
        {filteredGroups.length === 0 ? (
          <p className="p-6 text-center text-gray-500">No groups found.</p>
        ) : (
          <table className="w-full border-collapse">
            <thead className="bg-light-main text-light-text-alt sticky top-0">
              <tr>
                <th className="px-4 py-3 text-left font-bold">Group Name</th>
                <th className="px-4 py-3 text-left font-bold">Recipients</th>
                <th className="px-4 py-3 text-left font-bold">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredGroups.map((group) => {
                const isExpanded = expandedGroupId === group.id;
                return (
                  <React.Fragment key={group.id}>
                    <tr className="border-b border-light-border hover:bg-gray-50">
                      <td className="px-4 py-3 text-sm font-medium">
                        {group.name}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-600">
                        {group.users.length}
                      </td>
                      <td className="px-4 py-3">
                        <button
                          type="button"
                          onClick={() =>
                            setExpandedGroupId(isExpanded ? null : group.id)
                          }
                          className="px-3 py-1 bg-light-main text-white rounded text-xs font-semibold hover:bg-light-main-hover transition-colors"
                        >
                          {isExpanded ? 'Close' : 'Edit'}
                        </button>
                      </td>
                    </tr>

                    {/* Accordion */}
                    {isExpanded && (
                      <tr className="border-b border-light-border">
                        <td colSpan={3} className="px-6 py-4 bg-gray-50">
                          {/* Add Row */}
                          <div className="flex gap-2 mb-3">
                            <input
                              type="text"
                              placeholder="name@example.com"
                              value={newEmailInputs[group.id] || ''}
                              onChange={(e) =>
                                setNewEmailInputs((prev) => ({
                                  ...prev,
                                  [group.id]: e.target.value
                                }))
                              }
                              onKeyDown={(e) => {
                                if (e.key === 'Enter') {
                                  e.preventDefault();
                                  handleAddEmail(group.id);
                                }
                              }}
                              className="flex-1 border border-light-border p-2 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-light-main"
                            />
                            <button
                              type="button"
                              onClick={() => handleAddEmail(group.id)}
                              className="px-3 py-2 bg-light-main text-white rounded-md text-sm font-medium hover:bg-light-main-hover transition-colors"
                            >
                              Add
                            </button>
                          </div>

                          {/* Email List */}
                          {group.users.length === 0 ? (
                            <p className="text-sm text-gray-400">
                              No emails in this group.
                            </p>
                          ) : (
                            <ul className="space-y-1 max-h-48 overflow-y-auto">
                              {group.users.map((email) => (
                                <li
                                  key={email}
                                  className="flex items-center justify-between rounded border border-light-border px-3 py-2 bg-white"
                                >
                                  <span className="text-sm break-all">
                                    {email}
                                  </span>
                                  <button
                                    type="button"
                                    onClick={() =>
                                      handleRemoveEmail(group.id, email)
                                    }
                                    className="ml-3 shrink-0 text-red-500 hover:text-red-700 text-xs font-semibold transition-colors"
                                  >
                                    ✕
                                  </button>
                                </li>
                              ))}
                            </ul>
                          )}
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      {confirmation && <AdminConfirmationPopup {...confirmation} />}
    </div>
  );
};

export default EmailGroups;